import React, { useState } from 'react';
import { FiZap, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { submitCarrierDownloadRequest, CarrierApiError } from '../../services/carrierDownloadService';

type Status = 'idle' | 'loading' | 'success';

interface CarrierDownloadRequestProps {
  onSuccess?: () => void;
}

const FEATURE_TAGS = ['All carriers included', 'AI-powered sync', 'Real-time updates'];

const CarrierDownloadRequest: React.FC<CarrierDownloadRequestProps> = ({ onSuccess }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [conflictError, setConflictError] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);

  const handleRequest = async () => {
    setStatus('loading');
    setConflictError(false);
    setGenericError(null);

    try {
      await submitCarrierDownloadRequest();
      setStatus('success');
      onSuccess?.();
    } catch (error) {
      setStatus('idle');
      if (error instanceof CarrierApiError && error.statusCode === 409) {
        setConflictError(true);
      } else {
        setGenericError('Something went wrong. Please try again.');
      }
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setConflictError(false);
    setGenericError(null);
  };

  return (
    <div className="cdr-wrap">
      <div className="cdr-card">
        <div className="cdr-card__glow" />

        {status === 'idle' && (
          <div className="cdr-idle">
            {/* Badge */}
            <div className="cdr-badge">
              <span className="cdr-badge__dot" />
              CARRIER SYNC
            </div>

            {/* Two-column layout */}
            <div className="cdr-body">
              {/* LEFT — decorative icon */}
              <div className="cdr-icon-col">
                <div className="cdr-icon-ring">
                  <div className="cdr-icon-ring__orbit" />
                  <FiZap size={36} className="cdr-icon-ring__icon" />
                </div>
              </div>

              {/* RIGHT — text + button */}
              <div className="cdr-text-col">
                <h2 className="cdr-title">
                  Automated Carrier<br />
                  <span className="cdr-title__gradient">Download</span>
                </h2>
                <p className="cdr-subtitle">
                  Our system automatically syncs policy data from all connected carriers directly into your AMS — no manual entry, no missed updates.
                </p>

                {/* Conflict (409) banner */}
                {conflictError && (
                  <div className="cdr-alert cdr-alert--warning">
                    <FiAlertTriangle size={16} />
                    <span>You already have an active download request in progress.</span>
                  </div>
                )}

                {/* Generic error banner */}
                {genericError && (
                  <div className="cdr-alert cdr-alert--error">
                    <FiAlertTriangle size={16} />
                    <span>{genericError}</span>
                  </div>
                )}

                <button
                  className="cdr-btn"
                  onClick={handleRequest}
                  disabled={conflictError}
                >
                  <FiZap size={18} />
                  Request Carrier Download
                </button>

                <div className="cdr-tags">
                  {FEATURE_TAGS.map(tag => (
                    <span key={tag} className="cdr-tag">
                      <span className="cdr-tag__dot" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {status === 'loading' && (
          <div className="cdr-loading">
            <div className="cdr-spinner" />
            <p className="cdr-loading__text">Initiating carrier sync...</p>
            <p className="cdr-loading__sub">Connecting to carrier systems</p>
          </div>
        )}

        {status === 'success' && (
          <div className="cdr-success">
            <div className="cdr-success__icon">
              <FiCheck size={32} />
            </div>
            <h3 className="cdr-success__title">Request Submitted!</h3>
            <p className="cdr-success__desc">
              Your carrier download has been queued. We'll sync all policy data into your AMS shortly.
            </p>
            <button className="cdr-reset-btn" onClick={handleReset}>
              Submit Another Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarrierDownloadRequest;
